/**
 * 主题管理列表（旧 views/themes/index.vue）：筛选、分页、详情、新建、编辑、删除
 */
import { useEffect, useState } from 'react'
import { App, Badge, Button, Card, Select, Space, Table, Tag, type TableColumnsType } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import { MarkdownView } from '@/components/MarkdownView'
import { resolveThemeAssetUrl } from '@/api/services/themes'
import { ACTIVE_STATUS_OPTIONS, CATEGORY_FILTER_OPTIONS, LOCALE_OPTIONS, categoryColor, categoryLabel } from './constants'
import { useDeleteTheme, useThemeList } from './hooks'
import { ThemeModal } from './ThemeModal'
import { ThemeDetailModal } from './ThemeDetailModal'
import type { Theme, ThemeListFilters } from './types'
import styles from './ThemeListPage.module.css'

type ModalState = { type: 'create' } | { type: 'edit'; theme: Theme } | { type: 'detail'; theme: Theme } | null

export function ThemeListPage() {
  const { message, modal } = App.useApp()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState<ThemeListFilters>({})
  const [modalState, setModalState] = useState<ModalState>(null)
  const { data, isFetching, isError, refetch } = useThemeList(page, pageSize, filters)
  const deleteTheme = useDeleteTheme()

  useEffect(() => {
    if (isError) message.error('获取主题列表失败')
  }, [isError, message])

  // 改筛选条件时回到第一页
  const changeFilter = <K extends keyof ThemeListFilters>(key: K, value: ThemeListFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }))
    setPage(1)
  }

  const confirmDelete = (theme: Theme) => {
    modal.confirm({
      title: '确认删除主题',
      content: (
        <div className={styles.deleteWarning}>
          {`确定要彻底删除主题"${theme.title}"吗？此操作将：\n\n⚠️ 永久删除主题数据\n⚠️ 删除所有用户创建的实例\n⚠️ 删除所有关联的篇章内容\n\n此操作不可恢复，请谨慎操作！`}
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteTheme.mutateAsync(theme.id)
          message.success('主题及相关数据已彻底删除')
        } catch (error: any) {
          console.error('Failed to delete theme:', error)
          message.error(error.response?.data?.detail || '删除主题失败')
        }
      }
    })
  }

  const columns: TableColumnsType<Theme> = [
    {
      title: '主题信息',
      key: 'basic_info',
      width: 300,
      render: (_, record) => (
        <div className={styles.basicInfo}>
          {record.cover_image_url && (
            <img className={styles.cover} src={resolveThemeAssetUrl(record.cover_image_url)} alt="" />
          )}
          <div>
            <div className={styles.title}>
              <MarkdownView content={record.title} inline />
            </div>
            <div className={styles.code}>{record.code}</div>
            <div className={styles.hook}>{record.list_hook || '未填写列表钩子'}</div>
          </div>
        </div>
      )
    },
    {
      title: '分类',
      key: 'category',
      width: 120,
      render: (_, record) => <Tag color={categoryColor(record.category)}>{categoryLabel(record.category)}</Tag>
    },
    { title: '语言', key: 'locale', width: 80, render: (_, record) => <Tag color="blue">{record.locale}</Tag> },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Tag color={record.is_active ? 'success' : 'default'}>{record.is_active ? '启用' : '禁用'}</Tag>
      )
    },
    { title: '版本', key: 'version', width: 80, render: (_, record) => <Badge count={record.version} color="green" /> },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString()
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => setModalState({ type: 'detail', theme: record })}>
            详情
          </Button>
          <Button type="link" size="small" onClick={() => setModalState({ type: 'edit', theme: record })}>
            编辑
          </Button>
          <Button type="link" size="small" danger onClick={() => confirmDelete(record)}>
            删除
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="主题管理" subtitle="管理AI内容生成的主题模板，支持创建、编辑和监控主题使用情况" />

      <div className={styles.actionBar}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ type: 'create' })}>
            创建主题
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => {
              refetch()
            }}>
            刷新
          </Button>
        </Space>
        <Space wrap>
          <Select
            aria-label="选择分类"
            placeholder="选择分类"
            style={{ width: 120 }}
            allowClear
            value={filters.category}
            options={CATEGORY_FILTER_OPTIONS}
            onChange={(value) => changeFilter('category', value)}
          />
          <Select
            aria-label="选择语言"
            placeholder="选择语言"
            style={{ width: 100 }}
            allowClear
            value={filters.locale}
            options={LOCALE_OPTIONS}
            onChange={(value) => changeFilter('locale', value)}
          />
          <Select
            aria-label="状态"
            placeholder="状态"
            style={{ width: 100 }}
            allowClear
            value={filters.is_active}
            options={ACTIVE_STATUS_OPTIONS}
            onChange={(value) => changeFilter('is_active', value)}
          />
        </Space>
      </div>

      <Card variant="borderless">
        <Table<Theme>
          rowKey="id"
          columns={columns}
          dataSource={data?.themes}
          loading={isFetching}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(pagination) => {
            setPage(pagination.current ?? 1)
            setPageSize(pagination.pageSize ?? 20)
          }}
        />
      </Card>

      <ThemeModal
        open={modalState?.type === 'create' || modalState?.type === 'edit'}
        theme={modalState?.type === 'edit' ? modalState.theme : null}
        onClose={() => setModalState(null)}
      />
      <ThemeDetailModal
        theme={modalState?.type === 'detail' ? modalState.theme : null}
        onClose={() => setModalState(null)}
      />
    </div>
  )
}
